import { BotSequenceType } from "./BotSequence.Enums";
import { BOT_SEQUENCE_LEGACY_SENT_REASON, BotSequenceDeliveryKey, BotSequenceDeliveryOutcome } from "./BotSequenceScheduler.Interfaces";

// LINK docs/specs/sequence-suppressed-events/suppressed-events.md

/**
 * The three shapes a "due but not sent" decision takes in the ledger
 * (`bot_sequence_scheduled_attempt`). The family is derived from the row's `status`;
 * the reason is the status suffix (skips) or a `metadata` field (cancels / failures).
 */
export enum BotSequenceSuppressedOutcome {
  SKIPPED = "skipped",
  CANCELLED = "cancelled",
  FAILED = "failed"
}

/**
 * Skip reasons — the suffix of the `skipped:*` statuses the scheduler writes.
 * Owned by `BotSequenceScheduledAttemptStatus`; listed here as the page's filter set.
 */
export const BOT_SEQUENCE_SKIP_REASONS: readonly string[] = ["holiday", "superseded", "suppressed", "stale", "missed"];
/** A `skipped:stale` row whose day was already past when the enrollment was created (ENG-3216 decision 69) — rendered "Skipped: missed". */
export const BOT_SEQUENCE_MISSED_REASON = "missed";

/**
 * Cancel reasons — `metadata.cancelReason`, written by the fire recheck, the handler's
 * JIT/opt-out paths and the dispatcher's past-fireDate guard. The filter list is a
 * convenience, not a validation gate: a reason that appears in data before this list is
 * updated still renders with its raw string. `legacy_sent` (ENG-3512): the fire recheck found
 * the legacy engine already sent this step.
 */
export const BOT_SEQUENCE_CANCEL_REASONS: readonly string[] = [
  "enrollment_closed",
  "suppressed",
  "config_disabled",
  "past_fire_date",
  "outside_send_window",
  "jit_fetch_failed",
  "jit_entity_not_found",
  "opt_out",
  BOT_SEQUENCE_LEGACY_SENT_REASON,
  // ENG-3505: the handler's inline reconcile hit a unique violation — deterministic, so the attempt is cancelled instead of re-dispatched all day.
  "reconcile_failed",
  // ENG-3518: the attempt's user is soft-deleted or missing — cancelled before the JIT call.
  "user_deleted",
  // The handler's org recheck: the dispatcher flag was removed between the claim and the fire.
  "org_disabled",
  // The handler: the booked ladder day was removed or disabled in the config after booking.
  "step_removed",
  // The scheduler: an "Always send on this day" step whose day has no legal sending window.
  "must_send_no_legal_window"
];

/** The single reason of the `failed` family — the send was attempted and errored. */
export const BOT_SEQUENCE_FAILURE_REASON = "failed";

/** Stamped on a `cancelled` row whose `metadata.cancelReason` is missing — never hidden. */
export const BOT_SEQUENCE_UNKNOWN_REASON = "unknown";

/** Feed pagination contract, shared by both tabs. */
export const BOT_SEQUENCE_SUPPRESSED_EVENTS_DEFAULT_PAGE_SIZE = 50;
export const BOT_SEQUENCE_SUPPRESSED_EVENTS_MAX_PAGE_SIZE = 200;

/** Max span of the `localDay` date filter, in days (inclusive of both ends). */
export const BOT_SEQUENCE_SUPPRESSED_EVENTS_MAX_RANGE_DAYS = 90;

/**
 * The fixed zone every DERIVED time display and day computation on this page uses.
 * `localDay` and `anchorDate` are exempt — they are stored location-local and shown verbatim.
 */
export const BOT_SEQUENCE_SUPPRESSED_EVENTS_DISPLAY_TIMEZONE = "America/New_York";

/** FMS-side ids frozen in the enrollment anchor — an auditor's path into the FMS itself. */
export interface BotSequenceSubjectExternalIds {
  leaseId: string | null;
  leadId: string | null;
  tenantId: string | null;
  unitId: string | null;
  locationId: string | null;
}

/**
 * The attempt's state-transition stamps, verbatim from `metadata`. Rendered as the row's
 * timeline in the detail panel; keys absent from the row are absent here.
 */
export interface BotSequenceSuppressedEventTimeline {
  scheduledAt?: string;
  dispatchedAt?: string;
  sendingAt?: string;
  firedAt?: string;
  cancelledAt?: string;
  failedAt?: string;
  simulatedAt?: string;
  skippedHolidayAt?: string;
  skippedSupersededAt?: string;
  skippedSuppressedAt?: string;
  skippedStaleAt?: string;
}

/** One not-sent decision, assembled from the attempt + its enrollment + display joins. */
export interface BotSequenceSuppressedEventRow {
  attemptId: number;
  enrollmentId: number;
  orgId: number;
  orgName: string | null;
  locationId: number | null;
  locationName: string | null;
  userId: number;
  userName: string | null;
  /** From the enrollment; null for ABANDONED_CART by design (a cart is a lead-side fact). */
  tenantId: number | null;
  /** Subject for the lease-anchored types (PAST_DUE / MOVE_IN / MOVE_OUT). */
  leaseId: number | null;
  /** Subject for ABANDONED_CART. */
  leadId: number | null;
  sequenceType: BotSequenceType;
  stepOffset: number;
  /** The location-local business day the decision belongs to — displayed verbatim. */
  localDay: string;
  /** What was attempted (decision 65) — the per-channel ledger with recipients; null when the row never reached a send. */
  deliveries: Partial<Record<BotSequenceDeliveryKey, BotSequenceDeliveryOutcome>> | null;
  outcome: BotSequenceSuppressedOutcome;
  /** Taxonomy value, the raw string when unrecognized, or `unknown` for a cancel with no reason. */
  reason: string;
  /** `metadata.failureReason` — populated on `failed` rows. */
  errorDetail: string | null;
  /** ISO instant; the metadata stamp matching the row's status, else the row's `updatedAt`. */
  occurredAt: string;
  timeline: BotSequenceSuppressedEventTimeline;
  redispatchCount: number | null;
  /** Verbatim location-local day-0 of the enrollment's ladder. */
  anchorDate: string | null;
  externalIds: BotSequenceSubjectExternalIds;
  /** The ledger status, verbatim — the page never hides what it actually read. */
  rawStatus: string;
}

/** One enrollment currently on hold (`status = ACTIVE AND suppressUntil > now()`). */
export interface BotSequenceActiveSuppressionRow {
  enrollmentId: number;
  orgId: number;
  orgName: string | null;
  locationId: number | null;
  locationName: string | null;
  userId: number;
  userName: string | null;
  /** null for ABANDONED_CART by design. */
  tenantId: number | null;
  leaseId: number | null;
  leadId: number | null;
  sequenceType: BotSequenceType;
  /** ISO instant. */
  suppressUntil: string;
  commitmentChangeCount: number;
  /** Verbatim location-local day. */
  anchorDate: string;
  lastFiredAt: string | null;
  furthestStepFired: number | null;
  createdAt: string;
  updatedAt: string;
  externalIds: BotSequenceSubjectExternalIds;
}

export interface BotSequenceSuppressedEventsResponse {
  events: BotSequenceSuppressedEventRow[];
  total: number;
  page: number;
  pageSize: number;
  /** The range actually applied (echoed so the UI can show the resolved default). */
  from: string;
  to: string;
}

/** One row of the reason breakdown table, scoped to the selected sequence type. */
export interface BotSequenceSuppressedEventReasonCount {
  reason: string;
  outcome: BotSequenceSuppressedOutcome;
  count: number;
}

export interface BotSequenceSuppressedEventStats {
  /**
   * Event count per sequence type for the current filters — the segment badges.
   * Deliberately ignores the `sequenceType` filter (it is what the badges select) and the
   * `outcome`/`reason` drill.
   */
  byType: Record<BotSequenceType, number>;
  /** Scoped to the requested `sequenceType`; ignores the `outcome`/`reason` drill. */
  tiles: {
    total: number;
    skipped: number;
    cancelled: number;
    failed: number;
  };
  /** Scoped to the requested `sequenceType`, count desc, zero-count reasons omitted. */
  reasons: BotSequenceSuppressedEventReasonCount[];
  from: string;
  to: string;
}

/**
 * Server-side sort keys of the Active suppressions tab (ENG-3811): columns of the enrollment, or
 * the location / user it joins for display; `subject` = lease id, else lead id. The default is
 * `suppressUntil` ascending (what comes off hold next); `id` always breaks ties.
 */
export type BotSequenceActiveSuppressionsSortKey = "userName" | "locationName" | "subject" | "suppressUntil" | "commitmentChangeCount" | "updatedAt";

export interface BotSequenceActiveSuppressionsResponse {
  rows: BotSequenceActiveSuppressionRow[];
  total: number;
  page: number;
  pageSize: number;
  /**
   * Currently-suppressed enrollment count per sequence type for the shared filters — the
   * segment badges while this tab is active. Like the stats `byType`, it deliberately
   * ignores the `sequenceType` filter (it is what the badges select).
   */
  byType: Record<BotSequenceType, number>;
}
